import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MensagemRetidaDetalhe } from '@central/contracts';
import { CheckCircle2, Search, Trash2, UserPlus, Users } from 'lucide-react';
import { Button } from '@/components/Button';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { mensagemDeErro } from '@/lib/format';
import { listarClientes } from '@/features/clientes/clientes.api';
import {
  buscarSolicitantes,
  chavesDeRetidas,
  descartarMensagem,
  liberarMensagem,
} from '@/features/email/email.api';
import {
  consequenciaDaDecisao,
  impedimentoDaDecisao,
  nomeSugerido,
  type Decisao,
  type EstadoDaDecisao,
} from '@/features/email/retidas';

/**
 * As três decisões, num painel só.
 *
 * Três abas e não três botões espalhados: a escolha entre elas é **exclusiva** e
 * cada uma pede campos diferentes. Botões separados obrigariam a mostrar os três
 * formulários ao mesmo tempo, e o mais destrutivo dos três — descartar — ficaria
 * do lado do mais comum.
 *
 * A ordem é deliberada: cadastrar primeiro, porque é o caminho que resolve esta
 * mensagem **e** as próximas do mesmo endereço; descartar por último, com
 * variante de perigo, porque é o único que não produz chamado nenhum.
 */

const CLASSE_DE_CAMPO =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm ' +
  'transition-colors placeholder:text-muted-foreground focus-visible:border-primary ' +
  'focus-visible:shadow-glow focus-visible:outline-none';

interface Aba {
  chave: Decisao;
  rotulo: string;
  icone: typeof UserPlus;
}

const ABAS: readonly Aba[] = [
  { chave: 'cadastrar', rotulo: 'Cadastrar e abrir', icone: UserPlus },
  { chave: 'existente', rotulo: 'Abrir em nome de', icone: Users },
  { chave: 'descartar', rotulo: 'Descartar', icone: Trash2 },
];

export function PainelDeDecisao({
  mensagem,
  aoDecidir,
}: {
  mensagem: MensagemRetidaDetalhe;
  /** Fecha a janela e devolve o texto do que aconteceu, para o aviso da lista. */
  aoDecidir: (resultado: string) => void;
}) {
  const cliente = useQueryClient();
  const [decisao, setDecisao] = useState<Decisao>('cadastrar');
  const [contaId, setContaId] = useState<string | null>(mensagem.organizacaoSugerida?.contaId ?? null);
  const [nome, setNome] = useState(() => nomeSugerido(mensagem));
  const [solicitanteId, setSolicitanteId] = useState<string | null>(null);
  const [termo, setTermo] = useState('');
  const [motivo, setMotivo] = useState('');

  // Trocar de mensagem sem trocar de janela: sem isto, o nome digitado para uma
  // mensagem apareceria pré-preenchido na seguinte.
  useEffect(() => {
    setDecisao('cadastrar');
    setContaId(mensagem.organizacaoSugerida?.contaId ?? null);
    setNome(nomeSugerido(mensagem));
    setSolicitanteId(null);
    setTermo('');
    setMotivo('');
  }, [mensagem]);

  const estado: EstadoDaDecisao = { decisao, contaId, nome, solicitanteId, motivo };
  const impedimento = impedimentoDaDecisao(estado);

  /**
   * A lista de clientes exige `admin.accounts`, que quem tria pode não ter.
   *
   * O agente e o gestor da Central têm `email.triage` sem `admin.accounts` nem
   * `admin.contacts` — e para eles esta aba era um beco sem saída silencioso: o
   * seletor ficava vazio, o botão apagado, e o texto pedia para escolher um
   * cliente que a tela nunca ia listar. O 403 é resposta legítima aqui, não
   * erro de tela: em vez de mostrá-lo, escondemos a aba e explicamos.
   */
  const clientes = useQuery({
    queryKey: ['clientes', 'lista', '', ''],
    queryFn: () => listarClientes(),
    retry: false,
  });

  const podeCadastrar = !(clientes.error instanceof ApiError && clientes.error.status === 403);

  // Quem não pode cadastrar não deve nem abrir nessa aba: abrir num formulário
  // impossível é pior do que não oferecê-lo.
  useEffect(() => {
    if (!podeCadastrar && decisao === 'cadastrar') setDecisao('existente');
  }, [podeCadastrar, decisao]);

  const candidatos = useQuery({
    queryKey: ['retidas', 'candidatos', termo],
    queryFn: () => buscarSolicitantes(termo),
    enabled: decisao === 'existente' && termo.trim().length >= 2,
  });

  const decidir = useMutation({
    mutationFn: async (): Promise<string> => {
      if (decisao === 'descartar') {
        await descartarMensagem(mensagem.id, motivo.trim());
        return `Mensagem de ${mensagem.remetente} descartada. Ela continua guardada como prova.`;
      }
      if (decisao === 'cadastrar') {
        const r = await liberarMensagem(mensagem.id, {
          modo: 'NOVO_CONTATO',
          accountId: contaId!,
          name: nome.trim(),
        });
        return (
          `Chamado ${r.numero} aberto para ${r.solicitante.email}, agora cadastrado. ` +
          (r.outrasDoMesmoRemetente > 0
            ? `Ainda há ${r.outrasDoMesmoRemetente} mensagem(ns) deste remetente na fila.`
            : 'O próximo e-mail dele abre chamado sozinho.')
        );
      }
      const r = await liberarMensagem(mensagem.id, {
        modo: 'SOLICITANTE_EXISTENTE',
        requesterId: solicitanteId!,
      });
      return (
        `Chamado ${r.numero} aberto em nome de ${r.solicitante.nome}. ` +
        `Atenção: ${mensagem.remetente} continua sem cadastro e será retido de novo.`
      );
    },
    onSuccess: async (texto) => {
      // Invalida pela raiz: a lista, o detalhe e o contador do painel mudaram
      // todos ao mesmo tempo. Ver `chavesDeRetidas`.
      await cliente.invalidateQueries({ queryKey: chavesDeRetidas.raiz });
      aoDecidir(texto);
    },
  });

  const contas = clientes.data?.items ?? [];
  const contaEscolhida = contas.find((conta) => conta.id === contaId) ?? null;
  const pessoas = candidatos.data?.items ?? [];

  const rotuloDoBotao = useMemo(() => {
    if (decisao === 'descartar') return 'Descartar mensagem';
    if (decisao === 'cadastrar') return 'Cadastrar e abrir chamado';
    return 'Abrir chamado';
  }, [decisao]);

  return (
    <div className="space-y-4 rounded-lg border border-border bg-secondary/40 p-4">
      <div role="tablist" aria-label="O que fazer com esta mensagem" className="flex flex-wrap gap-1">
        {ABAS.filter((aba) => aba.chave !== 'cadastrar' || podeCadastrar).map((aba) => {
          const ativa = aba.chave === decisao;
          const perigo = aba.chave === 'descartar';
          return (
            <button
              key={aba.chave}
              type="button"
              role="tab"
              id={`aba-${aba.chave}`}
              aria-selected={ativa}
              aria-controls="painel-da-decisao"
              onClick={() => setDecisao(aba.chave)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
                'transition-colors focus-visible:shadow-glow focus-visible:outline-none',
                ativa
                  ? perigo
                    ? 'bg-destructive/10 text-destructive'
                    : 'bg-card text-foreground shadow-card'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <aba.icone className="h-4 w-4" aria-hidden />
              {aba.rotulo}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id="painel-da-decisao" aria-labelledby={`aba-${decisao}`}>
      {decisao === 'cadastrar' ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            <span className="font-mono text-xs text-foreground">{mensagem.remetente}</span> vira
            contato do cliente escolhido, e o chamado nasce em nome dele. O endereço vem da
            mensagem — não dá para digitar outro, e é de propósito.
          </p>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Cliente</span>
            <select
              className={CLASSE_DE_CAMPO}
              value={contaId ?? ''}
              onChange={(evento) => setContaId(evento.target.value || null)}
            >
              <option value="">Escolha o cliente…</option>
              {contas.map((conta) => (
                <option key={conta.id} value={conta.id}>
                  {conta.name} ({conta.code})
                </option>
              ))}
            </select>
          </label>

          {mensagem.organizacaoSugerida ? (
            <p className="text-xs text-muted-foreground">
              Sugerido pelo domínio <strong>{mensagem.organizacaoSugerida.dominio}</strong>, que
              hoje pertence a {mensagem.organizacaoSugerida.nome}.
            </p>
          ) : null}

          {/*
            O aviso que impede a fila de se realimentar.
            Cadastrar o remetente resolve UM endereço. Se o cliente escolhido
            não tem o domínio dele cadastrado, o colega da mesma empresa cai na
            fila amanhã — e alguém vai liberar um a um para sempre sem nunca
            perceber que o defeito está no cadastro.
          */}
          {contaEscolhida && mensagem.dominio && contaEscolhida.domain !== mensagem.dominio ? (
            <p className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-xs">
              {contaEscolhida.domain
                ? `${contaEscolhida.name} está cadastrado com o domínio ${contaEscolhida.domain}, diferente de ${mensagem.dominio}.`
                : `${contaEscolhida.name} está cadastrado sem domínio.`}{' '}
              Liberar aqui resolve <strong>esta</strong> mensagem, e só ela: qualquer outra pessoa
              de <span className="font-mono">{mensagem.dominio}</span> vai cair nesta fila de
              novo. Cadastre o domínio na ficha do cliente para as próximas abrirem chamado
              sozinhas.
            </p>
          ) : null}

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Nome do contato</span>
            <input
              className={CLASSE_DE_CAMPO}
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              placeholder="Como esta pessoa aparece na base do cliente"
            />
          </label>
        </div>
      ) : null}

      {decisao === 'existente' ? (
        <div className="space-y-3">
          {!podeCadastrar ? (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              Você tria a caixa, mas não cadastra contatos de cliente — por isso não há a opção
              de cadastrar o remetente. Abra em nome de alguém já cadastrado, ou peça a quem
              administra o cliente para cadastrar {mensagem.remetente} antes.
            </p>
          ) : null}
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">
              Quem responde por este chamado
            </span>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                aria-hidden
              />
              <input
                className={cn(CLASSE_DE_CAMPO, 'pl-8')}
                value={termo}
                onChange={(evento) => {
                  setTermo(evento.target.value);
                  setSolicitanteId(null);
                }}
                placeholder="Nome ou e-mail — a partir de duas letras"
              />
            </div>
          </label>

          {termo.trim().length >= 2 ? (
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {pessoas.map((pessoa) => (
                <li key={pessoa.id}>
                  <button
                    type="button"
                    onClick={() => setSolicitanteId(pessoa.id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm',
                      'transition-colors focus-visible:shadow-glow focus-visible:outline-none',
                      pessoa.id === solicitanteId
                        ? 'border-primary bg-card'
                        : 'border-transparent hover:bg-accent',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{pessoa.nome}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {pessoa.email}
                      </span>
                    </span>
                    <span className="flex-none text-xs text-muted-foreground">
                      {pessoa.interno ? 'Aptum' : (pessoa.cliente ?? 'Cliente')}
                    </span>
                  </button>
                </li>
              ))}
              {!candidatos.isPending && pessoas.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  Ninguém encontrado. Se a pessoa não existe, use “Cadastrar e abrir”.
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      ) : null}

      {decisao === 'descartar' ? (
        <label className="block space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Por que está descartando</span>
          <textarea
            rows={3}
            className={cn(CLASSE_DE_CAMPO, 'h-auto py-2')}
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            placeholder="Ex.: propaganda de fornecedor, sem relação com atendimento"
          />
        </label>
      ) : null}

      </div>

      <p className="text-xs text-muted-foreground">{consequenciaDaDecisao(estado)}</p>

      {decidir.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDeErro(decidir.error) ?? 'Não foi possível concluir a decisão.'}
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-3">
        {/* O motivo do bloqueio fica ao lado do botão, não escondido num
            tooltip: botão apagado sem explicação faz procurar o erro no lugar
            errado. */}
        {impedimento ? (
          <span className="text-xs text-muted-foreground">{impedimento}</span>
        ) : null}
        <Button
          variant={decisao === 'descartar' ? 'danger' : 'primary'}
          size="sm"
          disabled={impedimento !== null}
          loading={decidir.isPending}
          icon={<CheckCircle2 className="h-4 w-4" aria-hidden />}
          onClick={() => decidir.mutate()}
        >
          {rotuloDoBotao}
        </Button>
      </div>
    </div>
  );
}
